/**
 * safe-storage — discipline around browser localStorage / sessionStorage.
 *
 * Established 2026-05-27 after the storage-hygiene pass found
 * `cipherops:exportFilename` was stored in localStorage and could
 * embed a client name across sessions on shared workstations.
 *
 * # Policy
 *
 * 1. **NO authentication material in browser storage, ever.** Sessions
 *    are HttpOnly cookies. Tokens never live in JS-accessible storage.
 *    No exceptions.
 *
 * 2. **NO PII or PII-adjacent values in localStorage.** Anything that
 *    identifies a client, an engagement, a finding, a user — by name,
 *    by UUID, by filename — uses sessionStorage at most. localStorage
 *    is reserved for content-free UI preferences (booleans, enums,
 *    layout strings).
 *
 * 3. **Every storage write goes through this module.** The wrappers
 *    enforce the policy at the type level: `prefStorage` accepts only
 *    a small set of primitive types; `tabStorage` (sessionStorage)
 *    accepts arbitrary JSON. Direct `localStorage.setItem(...)` calls
 *    in screens are a code-review concern.
 *
 * 4. **Sign-out clears both stores.** ProfileScreen.handleSignOut and
 *    router.handleLock both call `localStorage.clear() +
 *    sessionStorage.clear()`. This is non-negotiable for shared-device
 *    safety.
 *
 * # Migration history
 *
 * 2026-05-27 — `cipherops:exportFilename` migrated from localStorage
 * to sessionStorage (was leaking last client/engagement filename
 * across sessions on shared workstations).
 *
 * # Why not move everything to the server?
 *
 * Per-user prefs (which tab is active in Report Builder, etc.) are
 * latency-critical and should NOT trigger an API round-trip on every
 * page load. localStorage is the right home — provided what's stored
 * is genuinely non-sensitive.
 *
 * Per-user state that IS sensitive (last engagement id) should sit in
 * memory + sessionStorage (cleared at tab close) or the server-side
 * `users` row (real preferences).
 */

// ── UI prefs (localStorage, content-free) ──────────────────────────────────

type Pref = boolean | string | number;

/**
 * Read a UI preference from localStorage. Returns `fallback` if absent
 * or if localStorage access throws (private browsing modes, quota).
 * Only use for content-free values (booleans, enums, layout keys).
 */
export function readPref<T extends Pref>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    if (typeof fallback === 'boolean') return (raw === '1') as T;
    if (typeof fallback === 'number') {
      const n = Number.parseFloat(raw);
      return (Number.isFinite(n) ? n : fallback) as T;
    }
    return raw as T;
  } catch {
    return fallback;
  }
}

/**
 * Write a UI preference to localStorage. Silently no-ops if storage
 * throws. Value type is constrained to content-free primitives so
 * callers can't accidentally persist user-identifiable data.
 */
export function writePref<T extends Pref>(key: string, value: T): void {
  try {
    if (typeof value === 'boolean') window.localStorage.setItem(key, value ? '1' : '0');
    else window.localStorage.setItem(key, String(value));
  } catch {
    /* private mode / quota — ignore */
  }
}

// ── Tab-scoped state (sessionStorage, cleared on tab close) ────────────────

/**
 * Read tab-scoped JSON state. Use for anything that may carry user-
 * identifiable data (last engagement id, draft form values, etc.) so
 * it auto-evaporates when the tab closes.
 */
export function readTabState<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeTabState(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

// ── Sign-out helper ────────────────────────────────────────────────────────

/**
 * Clear EVERYTHING on sign-out. Called by router.handleLock + the web
 * shell's sign-out handler. Returns silently on any error so the
 * sign-out navigation isn't blocked by a storage exception.
 */
export function clearAllBrowserStorage(): void {
  try { window.localStorage.clear(); } catch { /* ignore */ }
  try { window.sessionStorage.clear(); } catch { /* ignore */ }
  // Best-effort wipe of non-httpOnly cookies. The auth cookie is
  // httpOnly so JS can't reach it (the server clears it during the
  // /logout call); this catches analytics / preference cookies.
  try {
    document.cookie.split(';').forEach((c) => {
      const eq = c.indexOf('=');
      const name = (eq > -1 ? c.slice(0, eq) : c).trim();
      if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch { /* ignore */ }
}
